import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";

export type CompensationType =
  | "compensation"
  | "reward"
  | "other_credit"
  | "balance_transfer_credit"
  | "adjustment"
  | "regular"
  | "promo"
  | string;

export interface CompensationItem {
  operationId: number;
  operationPublicId?: string;
  userId: number;
  playerNickname?: string | null;
  playerEmail?: string | null;
  operatorId?: number | null;
  operatorName?: string | null;
  operatorEmail?: string | null;
  compensationType: CompensationType;
  amount: string;
  amountMinor?: number;
  currency: string;
  currencyScale?: number;
  statusCode: string;
  transactionCode?: string | null;
  bonusId?: string | number | null;
  bonusTitle?: string | null;
  bonusImage?: string | null;
  promoOrBonusId?: string | number | null;
  wagerMultiplier: string;
  internalComment?: string;
  createdAt: string;
  completedAt?: string;
}

export interface CompensationOrder {
  id?: "asc" | "desc";
}

export interface GetCompensationsPayload {
  limit: number;
  offset: number;
  order: CompensationOrder;
  operationId?: number;
  userIds?: number[];
  compensationTypes?: string[];
  transactionCode?: string;
  operationStatusCodes?: string[];
  createdFrom?: string;
  createdTo?: string;
  currencies?: string[];
  playerNickname?: string;
  operatorIds?: number[];
  amountFrom?: string;
  amountTo?: string;
}

interface PaymentsCompensationsState {
  list: CompensationItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
}

const initialState: PaymentsCompensationsState = {
  list: [],
  activePage: 1,
  totalPages: 0,
  totalItems: 0,
  isListFetched: false,
};

export const getCompensationsList = createAsyncThunk(
  "paymentsCompensations/list",
  async (payload: GetCompensationsPayload) => {
    try {
      const result = await instance.post(
        apiRoutes.paymentCompensationsList,
        payload,
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const paymentsCompensationsSlice = createSlice({
  name: "paymentsCompensations",
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
    },
    resetCompensationsList: (state) => {
      state.list = [];
      state.activePage = 1;
      state.totalPages = 0;
      state.totalItems = 0;
      state.isListFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCompensationsList.pending, (state) => {
      state.isListFetched = false;
    });
    builder.addCase(getCompensationsList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 0;
      state.totalPages = action.payload?.totalPages ?? 0;
    });
  },
});

export const { handlePaginate, resetCompensationsList } =
  paymentsCompensationsSlice.actions;
